import { apiService } from './apiService';

export interface TelstraInvoiceItem {
  id: string;
  invoiceNumber: string;
  period: string;
  amount: number;
  dueDate: string;
  status: 'Paid' | 'Pending' | 'Overdue';
}

// Expense management types (mobile-only)
export type TelstraMobileLineItem = {
  accountNumber: string;
  mobileNumber: string;
  userName: string;
  serviceActivationDate?: string;
  planName: string;
  monthlyFee: number;
  includedData: number;
  simNumber: string;
  deviceImei?: string;
  deviceModel?: string;
  deviceModelNumber?: string;
  deviceMake?: string;
  deviceOperatingSystem?: string;
  deviceCategory?: string;
  deviceType?: string;
  deviceAgeMonths?: number;
  latestMonthDataMb: number;
  latestMonthVoiceCalls: number;
  latestMonthVoiceMinutes: number;
  latestMonthSms: number;
  latestMonthBillTotal: number;
  avg3mDataMb: number;
  avg3mVoiceMinutes: number;
  avg3mBill: number;
  avg6mDataMb?: number;
  avg6mVoiceMinutes?: number;
  avg6mBill?: number;
  deviceTradeInValue?: number; // Device Trade In Indicative Value inc GST
  barRoaming?: string;
  barIdd?: string;
  // IR (International Roaming)
  irDataPackCode?: string;
  irDataPackDescription?: string;
  irDataPackMonthlyFee?: number;
  irVoicePackCode?: string;
  irVoicePackDescription?: string;
  irVoicePackMonthlyFee?: number;
  irActive?: string;
  avg3mIrDataMb?: number;
  // IDD (International Direct Dialing)
  iddPackIndicator?: string;
  iddPackCode?: string;
  iddPackDescription?: string;
  iddPackMonthlyFee?: number;
  avg3mIddExcess?: number;
  avg6mIddExcess?: number;
  // Stale service flags (YES/NO)
  noUsage3m?: string;
  noUsage6m?: string;
  noNetworkActivity3m?: string;
  noNetworkActivity6m?: string;
  onlyDiversionUsage3m?: string;
  onlyDiversionUsage6m?: string;
  onlyMessagebankDiversionUsage3m?: string;
  onlyMessagebankDiversionUsage6m?: string;
  // Location
  mostUsedSite?: string;
  mostUsedPostcode?: string;
  // Monthly history (12 months, newest first) extracted from BLITZ columns
  monthlyLabels?: string[];
  monthlyDataMb?: number[];
  monthlyVoiceCalls?: number[];
  monthlyVoiceMinutes?: number[];
  monthlySms?: number[];
  monthlyBillTotal?: number[];
  monthlyOverseasDataCalls?: number[];
  monthlyOverseasDataMinutes?: number[];
  monthlyOverseasDataMb?: number[];
  monthlyOverseasDataExcess?: number[];
};

export type TelstraMobileReport = {
  tenantId: string;
  partnerId: string;
  sourceFilename: string;
  statementMonth: string; // YYYY-MM
  generatedAt: string;
  customerName?: string; // Customer name from column D - used for validation before approval
  rows: TelstraMobileLineItem[];
  totals: {
    lines: number;
    totalMonthlyFees: number;
    totalLatestMonthBills: number;
    totalLatestMonthDataMb: number;
    totalIncludedDataMb?: number;
  };
};

class TelstraPartnerService {
  async uploadMobileReport(partnerId: string, tenantId: string, report: TelstraMobileReport, originalFile?: File): Promise<{ reportId: string; statementMonth: string }>{
    const originalWorkbookBase64 = originalFile ? await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1]);
      reader.onerror = () => reject(new Error('Could not read the original workbook'));
      reader.readAsDataURL(originalFile);
    }) : undefined;
    const createPayload = {
      originalWorkbookBase64,
      sourceFilename: report.sourceFilename,
      statementMonth: report.statementMonth,
      rows: report.rows,
      totals: report.totals,
    };
    
    try {
      const response = await (apiService as any).http.post(`/telstra/partner/${partnerId}/tenants/${tenantId}/reports`, createPayload);
      return response.data;
    } catch (err: any) {
      // apiService wraps axios errors into { message, status, details }
      const wrappedDetails = err?.details;
      const wrappedMessage = err?.message;
      const serverError = err?.response?.data;
      const errorMessage =
        wrappedMessage ||
        serverError?.error ||
        serverError?.message ||
        'Failed to upload report';
      const errorDetails =
        wrappedDetails
          ? (typeof wrappedDetails === 'string' ? wrappedDetails : JSON.stringify(wrappedDetails, null, 2))
          : (serverError?.details ? JSON.stringify(serverError.details, null, 2) : '');

      // Throw a more informative error
      throw new Error(errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage);
    }
  }

  async getLatestMobileReport(tenantId: string): Promise<TelstraMobileReport> {
    const response = await (apiService as any).http.get(`/telstra/partner/${tenantId}/reports/latest`);
    return response.data as TelstraMobileReport;
  }

  async listRecentMobileReportMonths(customerId: string, limit = 6): Promise<{ months: string[] }> {
    const response = await (apiService as any).http.get(`/telstra/partner/${customerId}/reports`, { params: { limit } });
    return response.data as { months: string[] };
  }

  async getMobileReportByMonth(customerId: string, month: string): Promise<TelstraMobileReport> {
    const response = await (apiService as any).http.get(`/telstra/partner/${customerId}/reports/${month}`);
    return response.data as TelstraMobileReport;
  }

  async deleteMobileReportByMonth(customerId: string, month: string): Promise<{ success: boolean }> {
    const response = await (apiService as any).http.delete(`/telstra/partner/${customerId}/reports/${month}`);
    return response.data as { success: boolean };
  }

  async deleteAllMobileReports(customerId: string): Promise<{ success: boolean }> {
    const response = await (apiService as any).http.delete(`/telstra/partner/${customerId}/reports`);
    return response.data as { success: boolean };
  }

  async uploadInvoice(
    customerId: string,
    file: File,
    metadata: { invoiceNumber: string; date: string; amount: number; dueDate: string; period: string },
  ): Promise<{ success: boolean }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('invoiceNumber', metadata.invoiceNumber);
    formData.append('date', metadata.date);
    formData.append('amount', metadata.amount.toString());
    formData.append('dueDate', metadata.dueDate);
    formData.append('period', metadata.period);

    const response = await (apiService as any).http.post(
      `/telstra/partner/${customerId}/invoices`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data as { success: boolean };
  }

  async listInvoices(customerId: string): Promise<TelstraInvoiceItem[]> {
    const response = await (apiService as any).http.get(`/telstra/partner/${customerId}/invoices`);
    return Array.isArray(response.data) ? response.data as TelstraInvoiceItem[] : [];
  }

  async downloadInvoice(customerId: string, invoiceId: string): Promise<Blob> {
    const response = await (apiService as any).http.get(
      `/telstra/partner/${customerId}/invoices/${encodeURIComponent(invoiceId)}/download`,
      { responseType: 'blob' },
    );
    return response.data as Blob;
  }

}

export const telstraPartnerService = new TelstraPartnerService();
export default telstraPartnerService;
